import React, { useEffect, useState, useRef } from "react";

interface DelayedRenderProps {
  /**
   * Content to show while loading
   */
  loadingContent: React.ReactNode;

  /**
   * Content to show when not loading and during the delay before showing loading content
   */
  children?: React.ReactNode;

  /**
   * Default content to show during initial loading before any children have been rendered
   * @default null
   */
  defaultContent?: React.ReactNode;

  /**
   * Whether loading is active
   */
  isLoading: boolean;

  /**
   * Delay before showing loading content (ms)
   * @default 300
   */
  delayMs?: number;

  /**
   * Minimum display time for loading content once shown (ms)
   * @default 1000
   */
  minDisplayTimeMs?: number;
}

/**
 * Component that delays rendering of loading state to prevent flickering
 * and ensures loading content is displayed for a minimum amount of time once shown.
 * When loading starts, it will continue to show the last children until the loading
 * indicator appears after the delay period.
 */
export const DelayedRender: React.FC<DelayedRenderProps> = ({
  loadingContent,
  children,
  isLoading,
  delayMs = 300,
  minDisplayTimeMs = 1000,
  defaultContent = null,
}) => {
  const [showLoading, setShowLoading] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
  const lastChildrenRef = useRef<React.ReactNode>(null);

  // Update the cached children whenever they change while not loading
  useEffect(() => {
    if (!isLoading) {
      lastChildrenRef.current = children;
    }
  }, [children, isLoading]);

  // Initialize lastChildrenRef with initial children
  useEffect(() => {
    if (lastChildrenRef.current === null) {
      lastChildrenRef.current = children;
    }
  }, [children]);

  useEffect(() => {
    let delayTimeout: NodeJS.Timeout | null = null;
    let minDisplayTimeout: NodeJS.Timeout | null = null;

    if (isLoading && !showLoading) {
      // Set a delay before showing loading content
      delayTimeout = setTimeout(() => {
        setShowLoading(true);
        setLoadingStartTime(Date.now());
      }, delayMs);
    } else if (!isLoading && showLoading && loadingStartTime !== null) {
      // Calculate how long the loading content has been shown
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, minDisplayTimeMs - elapsedTime);

      // Ensure loading content is shown for at least minDisplayTimeMs
      minDisplayTimeout = setTimeout(() => {
        setShowLoading(false);
        setLoadingStartTime(null);
      }, remainingTime);
    }

    return () => {
      if (delayTimeout) clearTimeout(delayTimeout);
      if (minDisplayTimeout) clearTimeout(minDisplayTimeout);
    };
  }, [isLoading, showLoading, loadingStartTime, delayMs, minDisplayTimeMs]);

  // When loading but not showing the loader yet, show the last rendered children
  if (isLoading && !showLoading) {
    return defaultContent || lastChildrenRef.current || null;
  }

  return showLoading ? loadingContent : children;
};
